import { useEffect } from "react";
import { Loader2 } from "lucide-react";

/**
 * Small Vaulzo-styled confirmation dialog (replaces window.confirm for
 * destructive actions). Escape or the backdrop cancels; while `busy` the
 * dialog stays open with a spinner and can't be dismissed mid-action.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Delete",
  busy = false,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-vaulzo-ink/30 px-6 animate-vz-fade"
      onMouseDown={() => !busy && onCancel()}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-[360px] rounded-[20px] border border-vaulzo-hairline bg-vaulzo-surface p-6 shadow-[0_18px_50px_rgba(23,24,26,0.18)]"
      >
        <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-vaulzo-ink">{title}</h2>
        {message && <p className="mb-0 mt-2 text-sm leading-relaxed text-vaulzo-faint">{message}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            autoFocus
            className="rounded-[11px] bg-vaulzo-row px-[18px] py-[10px] text-[14px] font-semibold text-vaulzo-muted transition-colors hover:bg-vaulzo-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex min-w-[92px] items-center justify-center rounded-[11px] bg-vaulzo-danger px-[18px] py-[10px] text-[14px] font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-danger-hover disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
